import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Linkedin, MessageCircle } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

const TikTokIcon = (props) => (
  <svg width={props.size || 15} height={props.size || 15} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.53 1h3.3v14.16a3.42 3.42 0 1 1-3.42-3.42h.36v3.3h-.36a.12.12 0 0 0 0 .24.13.13 0 0 0 .12-.12V1zm4.99 4.13a5.6 5.6 0 0 0 4.48 2.2v3.3a8.9 8.9 0 0 1-4.48-1.2v6.03a6.72 6.72 0 1 1-6.72-6.72c.28 0 .55.02.83.05v3.3a3.42 3.42 0 1 0 2.59 3.32V1h3.3v4.13z"/>
  </svg>
);
const SOCIAL_ICON = { facebook: Facebook, instagram: Instagram, youtube: Youtube, linkedin: Linkedin, whatsapp: MessageCircle, tiktok: TikTokIcon };
const SOCIAL_URL = {
  facebook:  (v) => v.startsWith("http") ? v : `https://facebook.com/${v}`,
  instagram: (v) => v.startsWith("http") ? v : `https://instagram.com/${v}`,
  youtube:   (v) => v.startsWith("http") ? v : `https://youtube.com/${v}`,
  tiktok:    (v) => v.startsWith("http") ? v : `https://tiktok.com/@${v.replace(/^@/, "")}`,
  linkedin:  (v) => v.startsWith("http") ? v : `https://linkedin.com/company/${v}`,
  twitter:   (v) => v.startsWith("http") ? v : `https://twitter.com/${v.replace(/^@/, "")}`,
  whatsapp:  (v) => v.startsWith("http") ? v : buildWhatsAppUrl(v),
};

export default function Footer() {
  const { t } = useTranslation();
  const { settings: brand } = useTheme();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const logoUrl = brand?.logo_url
    ? (brand.logo_url.startsWith("http") ? brand.logo_url : `${process.env.REACT_APP_BACKEND_URL || ""}${brand.logo_url}`)
    : null;
  const footer = brand?.footer || settings?.footer || {};
  const social = brand?.social || settings?.social || {};
  const columns = footer.columns || [];

  return (
    <footer className="bg-zinc-950 text-zinc-300 relative overflow-hidden grain" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            {logoUrl ? (
              <img src={logoUrl} alt={settings?.company_name || "Adelina"} className="w-11 h-11 object-contain bg-white rounded-full p-1" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                <span className="font-serif text-zinc-950 text-xl">A</span>
              </div>
            )}
            <div>
              <div className="font-serif text-2xl text-white">{settings?.company_name || "Adelina Employment Agency"}</div>
              <div className="text-[10px] tracking-[0.24em] uppercase" style={{color: "var(--brand-accent, #d4a017)"}}>Since 2013</div>
            </div>
          </div>
          <p className="text-zinc-400 max-w-md leading-relaxed mb-6">
            {footer.tagline || t("footer.tagline", settings?.tagline || "Trusted recruitment for a world of opportunity.")}
          </p>
          {/* Social icons */}
          {social && Object.keys(social).some((k) => social[k]) && (
            <div className="flex items-center gap-3">
              {Object.entries(social).map(([key, val]) => {
                if (!val || !SOCIAL_URL[key]) return null;
                const Icon = SOCIAL_ICON[key];
                if (!Icon) return null;
                return (
                  <a
                    key={key}
                    href={SOCIAL_URL[key](String(val))}
                    target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full border border-zinc-700 flex items-center justify-center hover:border-[color:var(--brand-accent)] hover:text-[color:var(--brand-accent)] transition-colors"
                    style={{ color: "var(--brand-accent, #d4a017)" }}
                    aria-label={key}
                    data-testid={`social-${key}`}
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {columns.length > 0
          ? columns.map((col, i) => (
              <div key={col.title || `col-${i}`}>
                <div className="overline mb-4" style={{color: "var(--brand-accent, #d4a017)"}}>{col.title}</div>
                <div className="flex flex-col gap-3 text-sm">
                  {(col.links || []).filter((lk) => !/^\/(faq|articles)(\/|$)/.test(lk.href || "")).map((lk) => (
                    <Link key={`${lk.href}-${lk.label}`} to={lk.href} className="hover:text-[color:var(--brand-accent,#d4a017)] transition-colors">
                      {lk.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))
          : (
            <>
              <div>
                <div className="overline mb-4">{t("footer.explore", "Explore")}</div>
                <div className="flex flex-col gap-3 text-sm">
                  <Link to="/about" className="hover:text-[color:var(--brand-accent)] transition-colors">{t("nav.about", "About Us")}</Link>
                  <Link to="/services" className="hover:text-[color:var(--brand-accent)] transition-colors">{t("nav.services", "Services")}</Link>
                  <Link to="/countries" className="hover:text-[color:var(--brand-accent)] transition-colors">{t("nav.countries", "Destinations")}</Link>
                  <Link to="/catalog" className="hover:text-[color:var(--brand-accent)] transition-colors">{t("nav.catalog", "Candidate Catalog")}</Link>
                </div>
              </div>
            </>
          )}

        <div>
          <div className="overline mb-4" style={{color: "var(--brand-accent, #d4a017)"}}>{t("footer.contact", "Contact")}</div>
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex items-start gap-2"><MapPin size={14} className="mt-1" style={{color:"var(--brand-accent)"}}/><span>{settings?.address || brand?.address || "Bandar Seri Begawan, Brunei"}</span></div>
            <div className="flex items-center gap-2"><Phone size={14} style={{color:"var(--brand-accent)"}}/><span>{settings?.phone || brand?.phone || "+673 812 3456"}</span></div>
            <div className="flex items-center gap-2"><Mail size={14} style={{color:"var(--brand-accent)"}}/><span>{settings?.email || brand?.email || "info@adelinaemployment.site"}</span></div>
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-400">
          <div>{footer.copyright || `© ${new Date().getFullYear()} Adelina Employment Agency. All rights reserved.`}</div>
          <div className="flex items-center gap-6">
            <Link to="/admin/login" className="hover:text-[color:var(--brand-accent)] transition-colors" data-testid="footer-admin-link">{t("footer.admin", "Admin")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
